import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Form, Card, Row, Col } from "react-bootstrap";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { GoogleLogin } from "@react-oauth/google";
import { authService } from "../../services/authService";
import { toast } from "react-toastify";
import ErrorIcon from "../../assets/images/fi_alert-triangle.svg";
import { GOOGLE_MAPS_API_KEY } from "../../config/ApiConfig";
import "../../styles/auth.css";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Email invalide")
    .required("Email requis"),
  password: Yup.string()
    .required("Mot de passe requis")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/,
      "Le mot de passe doit contenir au moins 8 caractères, une majuscule et un caractère spécial"
    ),
  prenom: Yup.string().required("Prénom requis"),
  nom: Yup.string().required("Nom requis"),
  telephone: Yup.string()
    .required("Numéro de téléphone requis")
    .matches(/^(06|07)\d{8}$/, "Numéro de téléphone invalide (format: 06/07 + 8 chiffres)"),
  adresse: Yup.string().required("Adresse de facturation requise"),
  societe: Yup.string(),
  tva: Yup.string().matches(
    /^(FR\d{11})?$/,
    "TVA invalide (format: FR + 11 chiffres)"
  ),
  siret: Yup.string(),
});

const Register = () => {
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isGMapsLoaded, setIsGMapsLoaded] = useState(false);
  const [address, setAddress] = useState("");
  const [latLong, setLatLong] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript) {
        // Script déjà en cours de chargement
        const checkGoogle = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            setIsGMapsLoaded(true);
            clearInterval(checkGoogle);
          }
        }, 100);
        return () => clearInterval(checkGoogle);
      } else {
        const gmapScriptEl = document.createElement("script");
        gmapScriptEl.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        gmapScriptEl.async = true;
        gmapScriptEl.defer = true;
        gmapScriptEl.onload = () => setIsGMapsLoaded(true);
        gmapScriptEl.onerror = () => {
          console.error("Erreur de chargement de Google Maps");
          setIsGMapsLoaded(false);
        };
        document.body.appendChild(gmapScriptEl);
      }
    } else {
      setIsGMapsLoaded(true);
    }
  }, []);

  const handleChangeAddress = (newAddress, setFieldValue) => {
    setAddress(newAddress);
    setFieldValue("adresse", newAddress);
  };

  const handleSelectAddress = async (selectedAddress, setFieldValue) => {
    setAddress(selectedAddress);
    try {
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);
      setLatLong(latLng);
      setFieldValue("adresse", selectedAddress, true);
      setFieldValue("latitude", latLng.lat);
      setFieldValue("longitude", latLng.lng);
    } catch (error) {
      console.error("Erreur lors du géocodage :", error);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError("");

      const data = {
        ...values,
        latitude: latLong.lat,
        longitude: latLong.lng,
      };

      const result = await authService.register(data);

      // Si l'API renvoie un token, l'utilisateur est automatiquement connecté
      if (result.data && result.data.token) {
        toast.success("Inscription réussie ! Bienvenue sur Marion Livraison.");
        setTimeout(() => {
          window.location.href = "/commande";
        }, 100);
      } else {
        // Sinon, rediriger vers la page de login
        history.push({
          pathname: "/login",
          state: { message: "Inscription réussie ! Tu peux maintenant te connecter." }
        });
      }
    } catch (err) {
      console.error("Erreur inscription:", err);
      const errorMessage = err.response?.data?.message ||
        "Une erreur est survenue lors de l'inscription. Veuillez réessayer.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError("");
    try {
      const result = await authService.googleLogin(credentialResponse.credential);

      // Si le profil est incomplet, rediriger vers la page de complétion
      if (result.profileIncomplete) {
        toast.info("Complète ton profil pour finaliser ton inscription");
        setTimeout(() => {
          window.location.href = "/complete-profile";
        }, 100);
      } else {
        toast.success("Inscription avec Google réussie !");
        setTimeout(() => {
          window.location.href = "/commande";
        }, 100);
      }
    } catch (err) {
      console.error("Erreur inscription Google:", err);
      setError(
        err.response?.data?.message ||
        "Une erreur est survenue lors de l'inscription avec Google. Veuillez réessayer."
      );
      toast.error(err.response?.data?.message || "Erreur d'inscription avec Google");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Erreur lors de l'inscription avec Google");
    toast.error("Erreur lors de l'inscription avec Google");
  };

  const searchOptions = {
    componentRestrictions: { country: ["fr", "mc"] },
    types: ["geocode", "establishment"],
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Card>
          <Card.Body className="p-5">
            <h2 className="text-center mb-4">Création de compte</h2>
            <p className="text-center text-muted mb-4">
              Crée ton compte pour commander tes livraisons
            </p>

            {error && (
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <img src={ErrorIcon} className="me-2" alt="error" width="20" />
                <span>{error}</span>
              </div>
            )}

            <Formik
              initialValues={{
                email: "",
                password: "",
                prenom: "",
                nom: "",
                telephone: "",
                adresse: "",
                latitude: "",
                longitude: "",
                societe: "",
                tva: "",
                siret: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                handleBlur,
                setFieldValue,
                isSubmitting,
              }) => (
                <Form onSubmit={handleSubmit}>
                  {/* Section 1: Compte */}
                  <h5 className="mb-3 text-primary">1. Informations de connexion</h5>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="email"
                          name="email"
                          className={`form-control ${
                            touched.email && errors.email ? "is-invalid" : ""
                          }`}
                          placeholder=" "
                        />
                        <label>Adresse e-mail *</label>
                        {touched.email && errors.email && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.email}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-floating mb-3 position-relative">
                        <Field
                          type={showPassword ? "text" : "password"}
                          name="password"
                          className={`form-control ${
                            touched.password && errors.password ? "is-invalid" : ""
                          }`}
                          placeholder=" "
                        />
                        <label>Mot de passe *</label>
                        <button
                          type="button"
                          className="btn btn-sm position-absolute end-0 top-50 translate-middle-y me-2"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ background: "none", border: "none" }}
                        >
                          {showPassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                        {touched.password && errors.password && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.password}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <div className="alert alert-info small mb-4">
                    <strong>Info:</strong> Le mot de passe doit contenir au moins 8 caractères, une majuscule et un caractère spécial
                  </div>

                  {/* Section 2: Informations personnelles */}
                  <h5 className="mb-3 text-primary mt-4">2. Informations personnelles</h5>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="prenom"
                          className={`form-control ${
                            touched.prenom && errors.prenom ? "is-invalid" : ""
                          }`}
                          placeholder=" "
                        />
                        <label>Prénom *</label>
                        {touched.prenom && errors.prenom && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.prenom}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="nom"
                          className={`form-control ${
                            touched.nom && errors.nom ? "is-invalid" : ""
                          }`}
                          placeholder=" "
                        />
                        <label>Nom *</label>
                        {touched.nom && errors.nom && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.nom}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="tel"
                          name="telephone"
                          className={`form-control ${
                            touched.telephone && errors.telephone ? "is-invalid" : ""
                          }`}
                          placeholder=" "
                        />
                        <label>Numéro de téléphone *</label>
                        {touched.telephone && errors.telephone && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.telephone}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      {isGMapsLoaded ? (
                        <PlacesAutocomplete
                          value={address}
                          onChange={(newValue) =>
                            handleChangeAddress(newValue, setFieldValue)
                          }
                          onSelect={(selected) =>
                            handleSelectAddress(selected, setFieldValue)
                          }
                          searchOptions={searchOptions}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                            loading,
                          }) => (
                            <div className="form-floating mb-3 position-relative">
                              <input
                                {...getInputProps({
                                  placeholder: " ",
                                  id: "adresse",
                                  className: `form-control ${
                                    touched.adresse && errors.adresse
                                      ? "is-invalid"
                                      : ""
                                  }`,
                                })}
                              />
                              <label htmlFor="adresse">Adresse de facturation *</label>
                              {suggestions.length > 0 && (
                                <div className="position-absolute w-100 bg-white border rounded shadow-sm autocomplete-dropdown" style={{ zIndex: 1000, top: "100%", maxHeight: "200px", overflowY: "auto" }}>
                                  {loading && <div className="p-2">Chargement...</div>}
                                  {suggestions.map((suggestion) => (
                                    <div
                                      {...getSuggestionItemProps(suggestion, {
                                        className: `p-2 cursor-pointer hover-bg-light ${suggestion.active ? 'active' : ''}`,
                                      })}
                                      key={suggestion.placeId}
                                    >
                                      {suggestion.description}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {touched.adresse && errors.adresse && (
                                <div className="text-danger mt-1 small d-flex align-items-center">
                                  <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                                  {errors.adresse}
                                </div>
                              )}
                            </div>
                          )}
                        </PlacesAutocomplete>
                      ) : (
                        <div className="form-floating mb-3">
                          <Field
                            type="text"
                            name="adresse"
                            id="adresse"
                            className={`form-control ${
                              touched.adresse && errors.adresse ? "is-invalid" : ""
                            }`}
                            placeholder=" "
                          />
                          <label htmlFor="adresse">Adresse de facturation *</label>
                          {touched.adresse && errors.adresse && (
                            <div className="text-danger mt-1 small d-flex align-items-center">
                              <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                              {errors.adresse}
                            </div>
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>

                  {/* Section 3: Informations entreprise (facultatif) */}
                  <h5 className="mb-3 text-primary mt-4">3. Informations entreprise (facultatif)</h5>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="societe"
                          className="form-control"
                          placeholder=" "
                        />
                        <label>Nom de la société</label>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="tva"
                          className={`form-control ${
                            touched.tva && errors.tva ? "is-invalid" : ""
                          }`}
                          placeholder=" "
                        />
                        <label>TVA (ex: FR12345678901)</label>
                        {touched.tva && errors.tva && (
                          <div className="text-danger mt-1 small d-flex align-items-center">
                            <img src={ErrorIcon} className="me-1" alt="error" width="16" />
                            {errors.tva}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="form-floating mb-3">
                        <Field
                          type="text"
                          name="siret"
                          className="form-control"
                          placeholder=" "
                        />
                        <label>SIREN</label>
                      </div>
                    </Col>
                  </Row>

                  <div className="d-grid gap-2 mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={isSubmitting || isLoading}
                    >
                      {isSubmitting ? "Création en cours..." : "Créer mon compte"}
                    </button>
                  </div>

                  <div className="text-center my-3">
                    <div className="d-flex align-items-center mb-3">
                      <hr className="flex-grow-1" />
                      <span className="mx-3 text-muted">ou</span>
                      <hr className="flex-grow-1" />
                    </div>

                    <div className="d-flex justify-content-center">
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        text="continue_with"
                        locale="fr"
                        shape="rectangular"
                        size="large"
                        width="100%"
                      />
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <p className="small text-muted">
                      Tu as déjà un compte ?{" "}
                      <a href="/login" className="text-primary">
                        Se connecter
                      </a>
                    </p>
                  </div>

                  <div className="mt-3">
                    <p className="small text-muted">
                      En créant un compte, tu acceptes nos{" "}
                      <a href="/cgu" target="_blank" rel="noopener noreferrer" className="text-primary">
                        Conditions Générales d'Utilisation
                      </a>
                      , nos{" "}
                      <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-primary">
                        Conditions Générales de Vente
                      </a>{" "}
                      ainsi que notre{" "}
                      <a href="/politique" target="_blank" rel="noopener noreferrer" className="text-primary">
                        Politique de Confidentialité
                      </a>
                      .
                    </p>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Register;
